import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Studio } from '@/api/client';
import { ContractorCombo, type ContractorValueItem } from '@/components/ContractorCombo/ContractorCombo';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { useToast } from '@/contexts/ToastContext';
import { STUDIOS_TABLE_QUERY_KEY } from '@/components/StudiosTable/studiosTableQueryKey';
import '@/components/CreatePersonModal/CreatePersonModal.css';

export interface StudioCatalogFormProps {
  /** null — режим создания; id — загрузка/редактирование */
  studioId: string | null;
  /** Смена ключа сбрасывает форму создания */
  createNonce: number;
  disabled?: boolean;
  /** Префилл названия при создании */
  nameSeed?: string | null;
  onSaved?: (studio: Studio) => void;
  /** Перейти к созданию новой студии (из режима редактирования) */
  onRequestCreate?: () => void;
}

export function StudioCatalogForm({
  studioId,
  createNonce,
  disabled = false,
  nameSeed = null,
  onSaved,
  onRequestCreate,
}: StudioCatalogFormProps) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [legalEntity, setLegalEntity] = useState<ContractorValueItem | null>(null);
  const [loading, setLoading] = useState(false);

  const resetCreate = useCallback(() => {
    setName((nameSeed ?? '').trim());
    setNotes('');
    setLegalEntity(null);
  }, [nameSeed]);

  useEffect(() => {
    if (!studioId) {
      resetCreate();
      return;
    }
    let cancelled = false;
    setLoading(true);
    void api.studios
      .get(studioId)
      .then((s) => {
        if (cancelled) return;
        setName(s.name);
        setNotes(s.notes?.trim() ?? '');
        setLegalEntity(
          s.legal_entity?.id ? { id: s.legal_entity.id, name: s.legal_entity.name } : null
        );
      })
      .catch((e) => {
        if (!cancelled) showError(e instanceof Error ? e.message : 'Не удалось загрузить студию');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studioId, showError, resetCreate]);

  useEffect(() => {
    if (studioId) return;
    resetCreate();
  }, [createNonce, studioId, resetCreate]);

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: STUDIOS_TABLE_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: ['studios'] });
    await queryClient.invalidateQueries({ queryKey: ['catalogManageSearch'] });
  }, [queryClient]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error('Укажите название студии');
      const body = {
        name: trimmed,
        notes: notes.trim() ? notes.trim() : null,
        legal_entity_id: legalEntity?.id ?? null,
      };
      if (studioId) return api.studios.update(studioId, body);
      return api.studios.create(body);
    },
    onSuccess: async (studio) => {
      showSuccess(studioId ? 'Студия сохранена' : 'Студия создана');
      await invalidate();
      onSaved?.(studio);
    },
    onError: (err: Error) => showError(err.message),
  });

  const busy = disabled || loading || saveMutation.isPending;

  return (
    <div className="create-person-modal__inner">
      {loading ? (
        <p className="create-person-modal__hint" aria-busy="true">
          Загрузка студии…
        </p>
      ) : (
        <>
          <Input
            placeholder="Название"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            fullWidth
            autoComplete="off"
          />
          <ContractorCombo
            value={legalEntity}
            onChange={setLegalEntity}
            source="legalEntity"
            placeholder="Юрлицо"
            disabled={busy}
            fullWidth
          />
          <Input
            placeholder="Заметки"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy}
            fullWidth
            autoComplete="off"
          />
        </>
      )}
      <div
        className={
          studioId && onRequestCreate
            ? 'create-person-modal__actions'
            : 'create-person-modal__actions create-person-modal__actions--single'
        }
      >
        {studioId && onRequestCreate ? (
          <Button
            fullWidth
            variant="outlined"
            type="button"
            disabled={busy}
            onClick={onRequestCreate}
          >
            Создать новую
          </Button>
        ) : null}
        <Button
          fullWidth
          variant="primary"
          type="button"
          disabled={busy}
          onClick={() => void saveMutation.mutateAsync()}
        >
          {saveMutation.isPending ? 'Сохранение…' : studioId ? 'Сохранить' : 'Создать студию'}
        </Button>
      </div>
    </div>
  );
}
